import {
  addDays,
  addMonths,
  addYears,
  differenceInYears,
  endOfDay,
  format as formatDate,
  format,
  getHours,
  getMilliseconds,
  getMinutes,
  isFuture,
  isValid,
  parse,
  set as setDate,
  subDays,
  subMinutes,
  subYears,
} from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { ARM_TIMEZONE, DATE_FORMAT, MIN_USER_AGE } from '../../constants';
import { TRAVEL_CALCULATION_DIFFERENCE_MINUTES } from '../../constants/travelCalcConstants';

export const getTimeDiffInMinutes = (timeString1, timeString2) => {
  // Convert time strings to minutes since midnight
  const time1 = parseInt(timeString1.split(':')[0], 10) * 60 + parseInt(timeString1.split(':')[1], 10);
  const time2 = parseInt(timeString2.split(':')[0], 10) * 60 + parseInt(timeString2.split(':')[1], 10);
  // Calculate time difference in minutes
  return time1 - time2;
};

export const getEndOfDay = (endDate) => {
  endDate.setHours(23, 59, 59, 999);
  return endDate;
};

export const convertToArm = (date) => zonedTimeToUtc(date, ARM_TIMEZONE);

export const convertArmToLocal = (armDate) => {
  const formatWithoutTZ = "yyyy-MM-dd'T'HH:mm:ss.SSS";
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return utcToZonedTime(formatInTimeZone(armDate, ARM_TIMEZONE, formatWithoutTZ), currentTimeZone);
};

export const formatDateTimeZone = (date, fmt, tz) => format(utcToZonedTime(date, tz),
  fmt,
  { timeZone: tz });

export const replaceTimeZone = (dateString, targetTimeZone) => {
  // Regular expression to match the timezone part (e.g., +05:00, -07:00, etc.)
  const timezoneRegex = /([+-]\d{2}:\d{2})$/;
  // Replace the matched timezone part with (+04:00)
  return dateString.replace(timezoneRegex, targetTimeZone);
  // Replace the time portion with 00:00:00 or 23:59:59
};

export const replaceTimePortion = (newDateString, isoFormat) => {
  const timeRegex = /T\d{2}:\d{2}:\d{2}/;

  return newDateString.replace(timeRegex, isoFormat);
};

export const getArmenianLocalDate = () => formatDateTimeZone(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSS", ARM_TIMEZONE);

export const getArmenianTomorrowStartDate = () => {
  const currentDate = getArmenianLocalDate();
  const startDate = setDate(new Date(currentDate), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  return addDays(startDate, 1);
};

export const formatTime = (time) => {
  if (!isValid(time)) {
    return '';
  }
  return format(time, 'HH:mm');
};

export const getStartDate = () => setDate(new Date(getArmenianLocalDate()), {
  hours: 0,
  minutes: 0,
  seconds: 0,
  milliseconds: 0,
});

const getEndDate = (startDate, days) => {
  // Adding number of days minus one minute (minus one minute due to APPA requirements)
  let endDate = addDays(startDate, days);
  endDate = subMinutes(endDate, 1);
  return endDate;
};

export const addOneMinuteExcludingSeconds = (date) => {
  const diff = date.getSeconds() * 1000 + date.getMilliseconds();
  date.setTime(date.getTime() - diff + 60 * 1000);
  return date;
};

export const getCmtplSuggestedDate = (timestamp) => {
  let suggested;
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();

  if (isFuture(date) && hours === 0 && minutes === 0) {
    suggested = date;
  } else if (isFuture(date) && hours === 23 && minutes === 59) {
    suggested = addOneMinuteExcludingSeconds(date);
  } else {
    suggested = date;
    suggested.setMinutes(suggested.getMinutes() + 15);
  }
  return suggested;
};

export const generateDateStrings = (days) => {
  const startDate = getStartDate();
  const endDate = getEndDate(startDate, days);
  return {
    startDate: formatDate(startDate, DATE_FORMAT),
    endDate: formatDate(endDate, DATE_FORMAT),
  };
};

// eslint-disable-next-line no-self-compare
export const isValidDate = (date) => date.getTime() === date.getTime();

export const generateDateString = (date) => date && formatDate(date, DATE_FORMAT);

export const getYearsAgo = (years) => subYears(new Date(), years);

export const getDiffInYearsFromNow = (date) => differenceInYears(new Date(), date);

export const getEarliestDateFromArray = (dates) => {
  const sortedDates = dates.slice().sort((a, b) => a.getTime() - b.getTime());
  return sortedDates[0];
};

export function tableDateFormat(value) {
  if (value) {
    let date = new Date(value);

    // NaN is not equal to NaN, we check if we have valid date.
    if (!isValidDate(date)) {
      date = parse(value, 'yyyy-MM-dd HH:mm:ss', new Date());
    }

    // NaN is not equal to NaN, we check if we have valid date.
    if (!isValidDate(date)) {
      date = parse(value, 'yyyy-MM-dd', new Date());
    }

    return format(date, 'dd/MM/yyyy');
  }
  return value;
}

export const reformatDate = (dateString, oldFormat, newFormat) => {
  const parsedDate = parse(dateString, oldFormat, new Date());
  return format(parsedDate, newFormat);
};

export const isDateAndTimeInFuture = (date, time) => {
  const dateAndTime = setDate(date, {
    hours: getHours(time),
    minutes: getMinutes(time),
    milliseconds: getMilliseconds(time),
  });
  return isFuture(dateAndTime);
};

export const getContractRangeDate = (range, key) => (range && range[key] ? format(range[key], 'dd-MM-yyyy') : '-');

export const checkIsAdult = (birthdate) => birthdate
    && new Date(birthdate) <= subYears(new Date(), MIN_USER_AGE);

export const calculateEndDate = (startDate, predefinedPackage) => {
  const result = subDays(startDate, 1);
  if (predefinedPackage.month) {
    return addMonths(result, predefinedPackage.month);
  }
  return addYears(result, predefinedPackage.year);
};

export const getCalendarMinStartDate = () => {
  const currentDate = new Date(getArmenianLocalDate());
  const endDate = endOfDay(currentDate);
  if (
    getTimeDiffInMinutes(
      endDate.toTimeString(), currentDate.toTimeString(),
    ) < TRAVEL_CALCULATION_DIFFERENCE_MINUTES
  ) {
    return addDays(currentDate, 1);
  }
  return currentDate;
};

export const getCalendarMinEndDate = (defaultMinDays) => addDays(
  new Date(getArmenianLocalDate()), defaultMinDays + 1,
);

export const isToday = (selectedDate) => {
  const today = new Date(getArmenianLocalDate());
  // Create a date object from the selected date
  const date = new Date(selectedDate);

  // Compare the year, month, and day
  return (
    date.getFullYear() === today.getFullYear()
      && date.getMonth() === today.getMonth()
      && date.getDate() === today.getDate()
  );
};
